import { useState, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { X, ArrowRight, Lightbulb } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useFeatureTour } from '@/hooks/useOnboarding'

interface FeatureSpotlightProps {
  tourId: string
  children: React.ReactNode
  title?: string
  description?: string
  position?: 'top' | 'bottom' | 'left' | 'right'
  showArrow?: boolean
  onDismiss?: () => void
  forceShow?: boolean
}

/**
 * Wraps a component and shows a spotlight tooltip on first view
 */
export function FeatureSpotlight({
  tourId,
  children,
  title,
  description,
  position = 'bottom',
  showArrow = true,
  onDismiss,
  forceShow = false,
}: FeatureSpotlightProps) {
  const { tour, shouldShow, dismiss } = useFeatureTour(tourId)
  const [isVisible, setIsVisible] = useState(false)
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 })
  const targetRef = useRef<HTMLDivElement>(null)
  const tooltipRef = useRef<HTMLDivElement>(null)

  const displayTitle = title || tour?.title
  const displayDescription = description || tour?.description
  const displayPosition = position || tour?.position || 'bottom'

  // Show spotlight after a short delay
  useEffect(() => {
    if (!forceShow && !shouldShow) return

    const timer = setTimeout(() => {
      setIsVisible(true)
    }, 1000)

    return () => clearTimeout(timer)
  }, [shouldShow, forceShow])

  // Calculate tooltip position
  useEffect(() => {
    if (!isVisible || !targetRef.current) return

    const updatePosition = () => {
      const rect = targetRef.current?.getBoundingClientRect()
      if (!rect) return

      const padding = 12
      const tooltipWidth = 320
      const vw = window.innerWidth
      let top = 0
      let left = 0

      switch (displayPosition) {
        case 'top':
          top = rect.top - padding
          left = rect.left + rect.width / 2
          break
        case 'bottom':
          top = rect.bottom + padding
          left = rect.left + rect.width / 2
          break
        case 'left':
          top = rect.top + rect.height / 2
          left = rect.left - padding
          break
        case 'right':
          top = rect.top + rect.height / 2
          left = rect.right + padding
          break
      }

      // P2 #19: Clamp to viewport to prevent off-screen rendering
      left = Math.max(tooltipWidth / 2 + padding, Math.min(left, vw - tooltipWidth / 2 - padding))
      top = Math.max(padding, Math.min(top, window.innerHeight - padding))

      setTooltipPosition({ top, left })
    }

    updatePosition()
    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition)
    }
  }, [isVisible, displayPosition])

  const handleDismiss = async () => {
    setIsVisible(false)
    await dismiss()
    onDismiss?.()
  }

  const getTooltipStyles = () => {
    const base = {
      position: 'fixed' as const,
      zIndex: 9999,
    }

    switch (displayPosition) {
      case 'top':
        return {
          ...base,
          bottom: `calc(100vh - ${tooltipPosition.top}px)`,
          left: tooltipPosition.left,
          transform: 'translateX(-50%)',
        }
      case 'bottom':
        return {
          ...base,
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          transform: 'translateX(-50%)',
        }
      case 'left':
        return {
          ...base,
          top: tooltipPosition.top,
          right: `calc(100vw - ${tooltipPosition.left}px)`,
          transform: 'translateY(-50%)',
        }
      case 'right':
        return {
          ...base,
          top: tooltipPosition.top,
          left: tooltipPosition.left,
          transform: 'translateY(-50%)',
        }
    }
  }

  const getArrowStyles = () => {
    const base = 'absolute w-3 h-3 bg-certify-600 rotate-45'
    
    switch (displayPosition) {
      case 'top':
        return cn(base, 'bottom-[-6px] left-1/2 -translate-x-1/2')
      case 'bottom':
        return cn(base, 'top-[-6px] left-1/2 -translate-x-1/2')
      case 'left':
        return cn(base, 'right-[-6px] top-1/2 -translate-y-1/2')
      case 'right':
        return cn(base, 'left-[-6px] top-1/2 -translate-y-1/2')
    }
  }

  return (
    <>
      <div ref={targetRef} className="relative">
        {children}
        {isVisible && (
          <div className="absolute inset-0 ring-4 ring-certify-400 ring-opacity-50 rounded-lg animate-pulse pointer-events-none" />
        )}
      </div>

      {isVisible &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/20 z-[9998]"
              onClick={handleDismiss}
            />

            {/* Tooltip */}
            <div
              ref={tooltipRef}
              style={getTooltipStyles()}
              className="max-w-sm"
            >
              <div className="relative bg-certify-600 text-white rounded-lg shadow-xl p-4">
                {showArrow && <div className={getArrowStyles()} />}

                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 p-2 bg-white/20 rounded-lg">
                    <Lightbulb className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    {displayTitle && (
                      <h4 className="font-semibold text-sm">{displayTitle}</h4>
                    )}
                    {displayDescription && (
                      <p className="text-sm text-white/90 mt-1">{displayDescription}</p>
                    )}
                    <Button
                      size="sm"
                      variant="secondary"
                      className="mt-3 bg-white text-certify-700 hover:bg-white/90"
                      onClick={handleDismiss}
                    >
                      Got it
                      <ArrowRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                  <button
                    onClick={handleDismiss}
                    className="flex-shrink-0 p-1 hover:bg-white/20 rounded transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </>,
          document.body
        )}
    </>
  )
}

/**
 * Standalone spotlight that targets an element by selector
 */
interface StandaloneSpotlightProps {
  tourId: string
  targetSelector: string
  title: string
  description: string
  position?: 'top' | 'bottom' | 'left' | 'right'
}

export function StandaloneSpotlight({
  tourId,
  targetSelector,
  title,
  description,
  position = 'bottom',
}: StandaloneSpotlightProps) {
  const { shouldShow, dismiss } = useFeatureTour(tourId)
  const [isVisible, setIsVisible] = useState(false)
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null)

  useEffect(() => {
    if (!shouldShow) return

    const timer = setTimeout(() => {
      const element = document.querySelector(targetSelector)
      if (element) {
        setTargetRect(element.getBoundingClientRect())
        setIsVisible(true)
      }
    }, 1500)

    return () => clearTimeout(timer)
  }, [shouldShow, targetSelector])

  // Update position on scroll/resize
  useEffect(() => {
    if (!isVisible) return

    const updatePosition = () => {
      const element = document.querySelector(targetSelector)
      if (element) {
        setTargetRect(element.getBoundingClientRect())
      }
    }

    window.addEventListener('resize', updatePosition)
    window.addEventListener('scroll', updatePosition)

    return () => {
      window.removeEventListener('resize', updatePosition)
      window.removeEventListener('scroll', updatePosition)
    }
  }, [isVisible, targetSelector])

  const handleDismiss = async () => {
    setIsVisible(false)
    await dismiss()
  }

  if (!isVisible || !targetRect) return null

  const getTooltipPosition = () => {
    const padding = 16
    const tooltipWidth = 320 // max-w-sm ≈ 320px
    const vw = window.innerWidth
    const isMobile = vw < 640

    // For targets near the right edge, position tooltip to the left instead
    const isNearRightEdge = targetRect.right + tooltipWidth / 2 > vw - padding

    switch (position) {
      case 'top':
        if (isMobile) {
          return {
            bottom: window.innerHeight - targetRect.top + padding,
            left: 16,
            right: 16,
            transform: 'none',
          }
        }
        if (isNearRightEdge) {
          return {
            bottom: window.innerHeight - targetRect.top + padding,
            right: padding,
            transform: 'none',
          }
        }
        return {
          bottom: window.innerHeight - targetRect.top + padding,
          left: targetRect.left + targetRect.width / 2,
          transform: 'translateX(-50%)',
        }
      case 'bottom':
        if (isMobile) {
          return {
            top: targetRect.bottom + padding,
            left: 16,
            right: 16,
            transform: 'none',
          }
        }
        if (isNearRightEdge) {
          return {
            top: targetRect.bottom + padding,
            right: padding,
            transform: 'none',
          }
        }
        return {
          top: targetRect.bottom + padding,
          left: targetRect.left + targetRect.width / 2,
          transform: 'translateX(-50%)',
        }
      case 'left':
        return {
          top: targetRect.top + targetRect.height / 2,
          right: window.innerWidth - targetRect.left + padding,
          transform: 'translateY(-50%)',
        }
      case 'right':
        return {
          top: targetRect.top + targetRect.height / 2,
          left: Math.min(targetRect.right + padding, vw - tooltipWidth - padding),
          transform: 'translateY(-50%)',
        }
    }
  }

  return createPortal(
    <>
      {/* Backdrop with cutout */}
      <div className="fixed inset-0 z-[9998]" onClick={handleDismiss}>
        <svg className="w-full h-full">
          <defs>
            <mask id="spotlight-mask">
              <rect width="100%" height="100%" fill="white" />
              <rect
                x={targetRect.left - 8}
                y={targetRect.top - 8}
                width={targetRect.width + 16}
                height={targetRect.height + 16}
                rx="8"
                fill="black"
              />
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            fill="rgba(0,0,0,0.5)"
            mask="url(#spotlight-mask)"
          />
        </svg>
      </div>

      {/* Highlight ring */}
      <div
        className="fixed z-[9999] pointer-events-none"
        style={{
          top: targetRect.top - 8,
          left: targetRect.left - 8,
          width: targetRect.width + 16,
          height: targetRect.height + 16,
        }}
      >
        <div className="w-full h-full rounded-lg ring-4 ring-certify-400 animate-pulse" />
      </div>

      {/* Tooltip */}
      <div
        className="fixed z-[10000] max-w-sm"
        style={getTooltipPosition()}
      >
        <div className="bg-certify-600 text-white rounded-lg shadow-xl p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 p-2 bg-white/20 rounded-lg">
              <Lightbulb className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-sm">{title}</h4>
              <p className="text-sm text-white/90 mt-1">{description}</p>
              <Button
                size="sm"
                variant="secondary"
                className="mt-3 bg-white text-certify-700 hover:bg-white/90"
                onClick={handleDismiss}
              >
                Got it
                <ArrowRight className="w-3 h-3 ml-1" />
              </Button>
            </div>
            <button
              onClick={handleDismiss}
              className="flex-shrink-0 p-1 hover:bg-white/20 rounded transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body
  )
}
